import React from "react";
import { Box, Container, Grid, Typography, Paper, Button } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const WhyJoinUs1 = () => {
  const highlights = [
    {
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
      title: "Collaborative Culture",
      desc: "Work in an environment where your voice is heard and teamwork is at the heart of everything we do.",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: "Growth Opportunities",
      desc: "We invest in your future with continuous learning programs and clear paths for career advancement.",
    },
    {
      icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
      title: "Innovation Driven",
      desc: "Be part of a team that challenges the status quo and embraces new technologies and ideas.",
    },
  ];

  const benefits = [
    {
      icon: <WorkspacePremiumIcon />,
      title: "Global Exposure",
      text: "Work with international clients and cross-border projects that broaden your perspective.",
    },
    {
      icon: <SupportAgentIcon />,
      title: "Mentorship",
      text: "Learn from industry veterans through our structured mentorship and training programs.",
    },
    {
      icon: <RocketLaunchIcon />,
      title: "Dynamic Environment",
      text: "Fast-paced and exciting projects that keep you engaged and constantly learning.",
    },
  ];

  return (
    <Box>
      {/* Introduction Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fbff" }}>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: "var(--primary-color)",
                    fontWeight: 800,
                    letterSpacing: 2,
                    display: "block",
                    mb: 2,
                  }}
                >
                  Join Our Journey
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontFamily: "'Roboto Slab', serif",
                    fontSize: { xs: "2.2rem", md: "3rem" },
                    color: "#222",
                    mb: 4,
                    lineHeight: 1.2,
                  }}
                >
                  Empowering{" "}
                  <span style={{ color: "var(--primary-color)" }}>
                    Your Future
                  </span>{" "}
                  at Vdoogar
                </Typography>
                <Typography
                  sx={{
                    color: "#555",
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    mb: 4,
                    textAlign: "justify",
                  }}
                >
                  At Vdoogar, we don't just offer jobs; we provide a platform
                  for excellence. Our team is composed of passionate
                  professionals dedicated to navigating the complexities of
                  global business. We believe in fostering a culture of trust,
                  innovation, and collective growth.
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {highlights.map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "20px",
                          bgcolor: "#fff",
                          display: "flex",
                          gap: 3,
                          transition: "0.3s",
                          "&:hover": {
                            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                          },
                        }}
                      >
                        <Box sx={{ color: "var(--primary-color)" }}>
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", lineHeight: 1.6 }}
                          >
                            {item.desc}
                          </Typography>
                        </Box>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: "#fff",
          color: "#222",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle pattern or graphic can go here */}
        <Container>
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontFamily: "'Roboto Slab', serif",
                color: "#222",
              }}
            >
              Why We Are Different
            </Typography>
            <Typography
              sx={{
                opacity: 0.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: "1.1rem",
                color: "#666",
              }}
            >
              We pride ourselves on providing a work environment that goes
              beyond the standard. Our commitment is to your holistic
              development.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: "24px",
                      bgcolor: "#fff",
                      border: "1px solid #eee",
                      height: "100%",
                      textAlign: "center",
                      color: "#222",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-10px)",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 2,
                        borderRadius: "50%",
                        bgcolor: "rgba(26, 66, 161, 0.1)",
                        mb: 3,
                        color: "var(--primary-color)",
                      }}
                    >
                      {React.cloneElement(benefit.icon, {
                        sx: { fontSize: 32 },
                      })}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, mb: 2, color: "#222" }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      sx={{ opacity: 0.8, lineHeight: 1.7, color: "#666" }}
                    >
                      {benefit.text}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{ py: { xs: 8, md: 12 }, textAlign: "center", bgcolor: "#f8fbff" }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: "#222",
                fontFamily: "'Roboto Slab', serif",
              }}
            >
              Ready to Take the{" "}
              <span style={{ color: "var(--primary-color)" }}>Next Step?</span>
            </Typography>
            <Typography
              sx={{ color: "#666", fontSize: "1.2rem", mb: 5, lineHeight: 1.8 }}
            >
              Explore our current opportunities and find a role where you can
              truly excel. Join a team that values your talent and supports your
              ambitions.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Link to="/currentOpenings" style={{ textDecoration: "none" }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "var(--primary-color)",
                    px: 4,
                    py: 1.8,
                    borderRadius: "50px",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#15358c" },
                  }}
                >
                  View Openings
                </Button>
              </Link>
              <Link to="/applyNow" style={{ textDecoration: "none" }}>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: "var(--primary-color)",
                    color: "var(--primary-color)",
                    px: 4,
                    py: 1.8,
                    borderRadius: "50px",
                    fontWeight: 700,
                    borderWidth: "2px",
                    "&:hover": { borderWidth: "2px" },
                  }}
                >
                  Apply Now
                </Button>
              </Link>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default WhyJoinUs1;
